import { Box, Flex, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { FiArrowRight } from 'react-icons/fi';

import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  getFormValues,
  getTableHeader,
  setClearSelectedAreasTable,
  setSelectedAreasProperties,
  getNoheadersStatus,
  handleActionStepNext,
  setPropertyMappingData,
} from 'redux/DataImportSlice';
import { GetTableProperties, getDimensionProperties } from 'redux/ItemPageSlice';
import Spinner from 'components/Spinner';

import colors from '../dataTheme';

import ActionStepFooter from './ActionStepFooter';
import { FieldLabel, StepFooterRow, StepHeading, StepSelect } from './actionStepUi';

/**
 * Step 3 of the dimension import wizard: property column mapping.
 *
 * The user maps each dimension property to a column from the import file.
 * On submit the form values are persisted to Redux and the wizard advances to
 * step 4 (removal configuration) without creating the action yet.
 */
const ActionStepThree = () => {
  const dispatch: any = useDispatch();
  const getFormValue = useSelector(getFormValues);
  const properties = useSelector(getDimensionProperties);
  const headerNames = useSelector(getTableHeader);
  const [loader, setLoader] = useState(false);
  const [formValue, setFormValue] = useState(getFormValue?.propertdata || {});
  const isHeader = useSelector(getNoheadersStatus);
  const [customHeaders, setCustomHeaders] = useState<any[]>(headerNames ?? []);

  useEffect(() => {
    if (isHeader) {
      const array: string[] = new Array(26)
        .fill(1)
        .map((_: string, index: number) => String.fromCodePoint(65 + index));
      const newHeaders = array
        .slice(0, headerNames.length)
        .map((item: string, headerIndex: number) => {
          return { current: `${headerNames[headerIndex]}`, modified: `Column ${item}` };
        });
      setCustomHeaders(newHeaders);
    } else {
      setCustomHeaders(
        headerNames.map((item: string) => {
          return { current: `${item}`, modified: `${item}` };
        }),
      );
    }
  }, [isHeader, headerNames]);

  const getTableProperties = async () => {
    setLoader(true);
    await dispatch(GetTableProperties({ selectedId: getFormValue?.dimension }));
    setLoader(false);
  };

  useEffect(() => {
    if (!getFormValue?.propertdata || Object.keys(getFormValue?.propertdata).length === 0) {
      getTableProperties();
    }
  }, []);

  const modifyFormValue = (key: any, value: any) => {
    const object: any = JSON.parse(JSON.stringify(formValue));
    object[`${key}`] = value;
    setFormValue(object);
  };

  useEffect(() => {
    if (!getFormValue?.propertdata || Object.keys(getFormValue?.propertdata).length === 0) {
      setFormValue((previousFormValue: any) => {
        const updatedFormValue = { ...previousFormValue };

        properties?.forEach((field: any) => {
          const matchingProperty = customHeaders.find(
            (item) => item?.current?.toLowerCase() === field?.name?.toLowerCase(),
          );

          const valueToSet = matchingProperty ? matchingProperty.current : null;
          dispatch(setSelectedAreasProperties({ [field.id]: valueToSet }));
          updatedFormValue[field.id] = valueToSet;
        });

        return updatedFormValue;
      });
    }
  }, [getFormValue?.propertdata, properties]);

  /**
   * Persist the property-to-column mapping in Redux and advance the wizard
   * to step 4 (removal configuration).  The action is NOT created here;
   * that happens in step 4 so removal settings can be included in a single
   * API call.
   */
  const handleSubmit = async (form: any, setSubmitting: (data: boolean) => void) => {
    dispatch(setPropertyMappingData(form));
    await setSubmitting(false);
    dispatch(setClearSelectedAreasTable());
    dispatch(handleActionStepNext());
  };
  return (
    <Box w='100%'>
      <StepHeading
        title='Select properties'
        subtitle='Match each dimension property to a column in your file.'
      />
      {loader ? (
        <Flex direction='column' alignItems='center' justifyContent='center' py={10}>
          <Spinner styleProperties={{ my: '4vh' }} />
        </Flex>
      ) : (
        <Formik
          initialValues={formValue}
          onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
          enableReinitialize={true}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form style={{ width: '100%' }}>
              {Object.keys(formValue).length > 0 ? (
                <Flex direction='column' gap={3}>
                  <Flex alignItems='center' gap={3}>
                    <FieldLabel flex={1} mb={0}>
                      Property in Blox
                    </FieldLabel>
                    <Box w='16px' />
                    <FieldLabel flex={1} mb={0}>
                      Column in file
                    </FieldLabel>
                  </Flex>
                  {properties.map((item: any) => (
                    <Flex key={item?.id} alignItems='center' gap={3}>
                      <Text
                        flex={1}
                        minW={0}
                        {...PLAN_TEXT.bodyMedium}
                        color={colors.textDark}
                        noOfLines={1}
                        title={item.name}
                      >
                        {item.name}
                      </Text>
                      <FiArrowRight size={16} color={colors.textSecondary} />
                      <Box flex={1} minW={0}>
                        <StepSelect
                          value={values[item.id]}
                          placeholder='Select…'
                          isClearable
                          options={customHeaders
                            .filter(
                              (header: any) =>
                                header?.current === values[item.id] ||
                                !Object.values(formValue).includes(header?.current),
                            )
                            .map((header: any) => ({
                              value: header.current,
                              label: header.modified,
                            }))}
                          onChange={(role) => {
                            modifyFormValue(item.id, role);
                            setFieldValue(item.id, role);
                            dispatch(setSelectedAreasProperties({ [item.id]: role }));
                          }}
                        />
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text {...PLAN_TEXT.body} color={colors.textSecondary}>
                  No properties found
                </Text>
              )}

              <StepFooterRow>
                <ActionStepFooter
                  nextButtonName='Continue'
                  prevButtonName='Previous'
                  isSubmitting={isSubmitting}
                />
              </StepFooterRow>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default ActionStepThree;
